import { APIError } from 'types';

/**
 * `IFetchDataResponse.error` is the *raw* error returned by the api. It is only a string in the
 * few cases where a gateway writes a bare message as the body - most of the time it is a parsed
 * RFC 7807 `ProblemDetails` object, an empty object (empty error body) or a caught `Error`.
 * Passing it straight to a caption renders it as "[object Object]", so every user facing message
 * must go through {@link getApiErrorMessage} first.
 */
export interface ApiErrorResponse {
  error?: APIError | string;
  status?: number;
}

/**
 * Messages longer than this are api/proxy payloads (html error pages, stack traces) rather than
 * something a user can act on, so the caller's fallback is used instead.
 */
const MAX_MESSAGE_LENGTH = 300;

const asMessage = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();

  return trimmed && trimmed.length <= MAX_MESSAGE_LENGTH ? trimmed : undefined;
};

/**
 * Resolves the message to show the user for a failed api call.
 *
 * A server side failure (5xx) never carries anything actionable - the body is the generic
 * "An error occurred while processing your request." plus a traceId - and a missing status
 * (`-1`) means the request never got a response, so both resolve to the fallback.
 *
 * @param response the failed `IFetchDataResponse`
 * @param fallback translation key used when the api did not provide a usable message
 */
export const getApiErrorMessage = ({ error, status }: ApiErrorResponse, fallback: string): string => {
  if (!status || status <= 0 || status >= 500) {
    return fallback;
  }

  const message = typeof error === 'string' ? error : error?.detail ?? error?.title;

  return asMessage(message) ?? fallback;
};

/**
 * Reads the field level messages out of an RFC 9457 `ValidationProblemDetails` body.
 *
 * The api declares `Errors` as an `IDictionary<string, string[]>`, so `Object.values` on it
 * yields a `string[][]`. That has to be flattened before it can be used as a caption - the
 * toast store's caption guard rejects a nested array and shows the generic server error
 * instead of the message the api sent.
 *
 * @param response the failed `IFetchDataResponse`
 * @param fallback translation key used when the api did not provide a usable message
 * @returns one caption per field level message, or {@link getApiErrorMessage} when there are none
 */
export const getApiFieldErrors = (response: ApiErrorResponse, fallback: string): string | string[] => {
  const { error } = response;
  const errors = typeof error === 'string' ? undefined : error?.errors;

  const messages = Object.values(errors ?? {})
    .flat()
    .map(asMessage)
    .filter((message): message is string => Boolean(message));

  return messages.length > 0 ? messages : getApiErrorMessage(response, fallback);
};
